import { ReactElement } from 'react';

import clsx from 'clsx';

import { ReactComponent as FacebookLogo } from '@@/assets/facebook.svg';
import { ReactComponent as LinkedInLogo } from '@@/assets/linkedIn.svg';
import { ReactComponent as PhoneIcon } from '@@/assets/phone.svg';
import HorizontalRule from '@@/components/HorizontalRule';

import HoursOfOperation from '../../HoursOfOperation';
import Link from '../../Link';
import PhoneLink from '../../PhoneLink';

import classes from './ToyotaFooterHeader.module.css';

type TLink = { label: string; to: string };

type TLinkGroup = {
  header: string;
  links: TLink[];
};

const toyotaInfo = {
  phoneNumber: '1-888-492-1780',
  hours: [
    {
      label: 'Mon - Fri: 9am - 12am EST',
      machineReadable: 'Mo-Fr 09:00-23:59:59',
    },
    {
      label: 'Sat: 10am - 9pm EST',
      machineReadable: 'Sa 10:00-21:00',
    },
  ],
} as const;

const linkGroups: TLinkGroup[] = [
  {
    header: 'About',
    links: [
      {
        label: 'Why Toyota Auto Insurance',
        to: 'https://toyotaims.com/toyotaautoinsurance#tai-why',
      },
      {
        label: 'Products',
        to: 'https://toyotaims.com/toyotaautoinsurance#tai-product',
      },
      {
        label: 'Availability',
        to: 'https://toyotaims.com/toyotaautoinsurance#tai-availability',
      },
      {
        label: 'FAQs',
        to: process.env.REACT_APP_FAQ_URL!,
      },
    ],
  },
  {
    header: 'Toyota Websites',
    links: [
      {
        label: 'Toyota.com',
        to: 'https://www.toyota.com/',
      },
      {
        label: 'Toyota Financial Services',
        to: 'https://www.toyotafinancial.com/',
      },
      {
        label: 'Toyota Owners',
        to: 'https://www.toyota.com/owners',
      },
      {
        label: 'Toyota Insurance Management Solutions',
        to: 'https://toyotaims.com/',
      },
    ],
  },
  {
    header: 'Resources',
    links: [
      {
        label: 'Contact Us',
        to: process.env.REACT_APP_CONTACT_URL!,
      },
      {
        label: 'Privacy Policy',
        to: '/privacy',
      },
      {
        label: 'Terms & Conditions',
        to: '/terms',
      },
      {
        label: 'Do Not Sell My Info',
        to: '/privacy#do-not-sell-my-personal-information',
      },
    ],
  },
];

const socialLinks = {
  header: 'Follow',
  links: [
    {
      label: 'Facebook',
      to: 'https://www.facebook.com/ToyotaIMS',
      logo: <FacebookLogo />,
    },
    {
      label: 'LinkedIn',
      to:
        'https://www.linkedin.com/company/toyota-insurance-management-solutions-usa/',
      logo: <LinkedInLogo />,
    },
  ],
};

function LinkGroup({ header, links }: TLinkGroup) {
  return (
    <div key={header} className={classes.link_group}>
      <div className={classes.link_group_header}>{header}</div>
      <ul className={classes.link_group_list}>
        {links.map((link) => (
          <li key={link.label}>
            <Link to={link.to} className={classes.link_group_link}>
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ToyotaFooterHeader({ logo }: { logo: ReactElement }) {
  return (
    <div className={classes.root}>
      <div className={classes.link_group_container}>
        <div className={clsx(classes.link_group, classes.toyota_group)}>
          {logo}

          <PhoneLink
            phoneNumber={toyotaInfo.phoneNumber}
            className={classes.phone_number}
          >
            {({ parsedNumber: phone }) => (
              <>
                <PhoneIcon className={classes.phone_icon} />
                <span>{`(${phone?.areaCode}) ${phone?.firstThree} ${phone?.lastFour}`}</span>
              </>
            )}
          </PhoneLink>

          <div className={classes.hours_of_operation}>
            {toyotaInfo.hours.map((hoursProps) => (
              <HoursOfOperation {...hoursProps} key={hoursProps.label} />
            ))}
          </div>
        </div>

        {linkGroups.map((group) => (
          <LinkGroup key={group.header} {...group} />
        ))}

        <div className={clsx(classes.link_group, classes.social_group)}>
          <span className={classes.link_group_header}>
            {socialLinks.header}
          </span>
          <ul className={classes.social_links}>
            {socialLinks.links.map((link) => (
              <li className={classes.socialItem} key={link.to}>
                <Link
                  to={link.to}
                  className={classes.social_link}
                  aria-label={link.label}
                >
                  {link.logo}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <HorizontalRule className={classes.hr} />
    </div>
  );
}
